import disposalData from "../data/special/disposal.json";

import {
  ResponsiveContainer,
  Sankey,
  Tooltip,
  Layer,
  Rectangle,
} from "recharts";
import { pseudoRandom } from "../utils";

interface IDisposalChartData {
  nodes: string[];
  links: {
    source: number;
    target: number;
    value: number;
  }[];
}

interface IProps {
  data: IDisposalChartData;
  width?: number;
  height?: number;
  forRender?: boolean;
}

function hslToRgb(h: number, s: number, l: number) {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function contrastRatio(rgb1: number[], rgb2: number[]) {
  const luminance = (rgb: number[]) => {
    return [0.2126, 0.7152, 0.0722].reduce((acc, c, i) => {
      const channel = rgb[i] / 255;
      const value =
        channel <= 0.03928
          ? channel / 12.92
          : Math.pow((channel + 0.055) / 1.055, 2.4);
      return acc + value * c;
    }, 0);
  };

  const L1 = luminance(rgb1);
  const L2 = luminance(rgb2);
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

function getContrastingColor(seed: number) {
  let color;
  for (let i = 0; i < 20; i++) {
    const newSeed = seed * 1000 + i;
    const hue = Math.floor(pseudoRandom(newSeed) * 360);

    const saturation = 80;
    const lightness = Math.floor(pseudoRandom(newSeed + 1) * 41) + 30; // 30%–70%

    const rgb = hslToRgb(hue / 360, saturation / 100, lightness / 100);
    const contrastWithWhite = contrastRatio(rgb, [255, 255, 255]);
    const contrastWithBlack = contrastRatio(rgb, [0, 0, 0]);

    if (contrastWithWhite >= 2.5 && contrastWithBlack >= 2.5) {
      color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      break;
    }
  }
  return color || `hsl(0, 0%, 50%)`; // fallback: neutral gray
}

export default function DisposalChart({
  data,
  forRender,
  width,
  height,
}: IProps) {
  const MyCustomNode = ({
    x,
    y,
    width,
    height,
    index,
    payload,
    containerWidth,
  }: any) => {
    const isOut = x + width + 6 > containerWidth;

    return (
      <Layer key={`CustomNode${index}`}>
        <Rectangle
          x={x}
          y={y - 2}
          width={width}
          height={height + 4}
          fill="#5192ca"
          fillOpacity="1"
        />
        <text
          textAnchor={isOut ? "end" : "start"}
          x={isOut ? x - 6 + 45 : x + width + 6 + 45}
          y={y + height / 2 + 5}
          fontSize="18"
          stroke="#333"
          strokeOpacity="0.4"
        >
          {payload.value}
        </text>
        <image
          xlinkHref={`img/icons/${(disposalData as any)[payload.key]?.icon}`}
          width={50}
          height={50}
          x={isOut ? x - 6 : x + width}
          y={y + height / 2 - 25}
        />
      </Layer>
    );
  };

  const MyCustomLink = ({
    sourceX,
    sourceY,
    sourceControlX,
    targetX,
    targetY,
    targetControlX,
    linkWidth,
    payload,
  }: any) => {
    const seed = Number(payload.source.key) + Number(payload.target.key) * 100;
    const hue = Math.floor(pseudoRandom(seed) * 360);

    return (
      <path
        className="recharts-sankey-link"
        d={`
          M${sourceX},${sourceY}
          C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
        `}
        fill="none"
        //stroke={`hsl(${hue}, 100%, 30%)`}
        stroke={getContrastingColor(seed)}
        strokeWidth={linkWidth + 2}
        strokeOpacity="0.6"
      />
    );
  };

  if (data.links.length === 0 || data.nodes.length === 0) {
    return <div />;
  }

  const additionalProps = forRender ? { width, height } : {};

  const chart = (
    <Sankey
      data={data}
      node={<MyCustomNode />}
      link={<MyCustomLink />}
      nodePadding={32}
      margin={{
        right: 90,
        bottom: 20,
        top: 20,
      }}
      {...additionalProps}
    >
      <Tooltip />
    </Sankey>
  );

  if (forRender) {
    return chart;
  }

  return <ResponsiveContainer height={800}>{chart}</ResponsiveContainer>;
}
